/**
 * useCreateBookingForm — owns all CreateBookingModal field + status state.
 * Mirrors the `useScheduleWorkshopForm` split (state hook separate from the
 * shell/fields) so no single file trips the repo's line cap.
 */
import { useEffect, useState } from 'react';

import { fetchBookingConfig } from '../../../api/bookingsV2';

import {
  computeMoneySummary,
  validateCreateBookingForm,
  type MoneySummary,
  DEFAULT_PAYMENT_MODE,
  type PaymentMode,
  type PickedWorkshop,
} from './CreateBookingModalHelpers';

export interface CreateBookingFormApi {
  workshop: PickedWorkshop | null;
  setWorkshop: (w: PickedWorkshop | null) => void;
  fullName: string;
  setFullName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  tickets: number;
  setTickets: (v: number) => void;
  /** Fill the ticket count to the workshop's full seats-left ("All N"). */
  setAllTickets: () => void;
  mode: PaymentMode;
  setMode: (v: PaymentMode) => void;
  reason: string;
  setReason: (v: string) => void;
  isSubmitting: boolean;
  setIsSubmitting: (v: boolean) => void;
  error: string | null;
  setError: (v: string | null) => void;
  money: MoneySummary;
  validationError: string | null;
}

export function useCreateBookingForm(): CreateBookingFormApi {
  const [workshop, setWorkshopState] = useState<PickedWorkshop | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [tickets, setTicketsState] = useState(1);
  const [mode, setMode] = useState<PaymentMode>(DEFAULT_PAYMENT_MODE);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether checkout adds a service fee. Null until it loads (or if the fetch
  // fails), in which case the summary still mentions the fee.
  const [serviceFeeEnabled, setServiceFeeEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    fetchBookingConfig()
      .then((cfg) => {
        if (active) setServiceFeeEnabled(cfg.serviceFeeEnabled);
      })
      .catch(() => {
        /* leave null — the summary mentions the fee in case it applies */
      });
    return () => {
      active = false;
    };
  }, []);

  // Picking a new workshop re-clamps tickets to its (possibly smaller) seats
  // left, defaulting a fresh pick to 1 rather than carrying over a stale count.
  const setWorkshop = (w: PickedWorkshop | null) => {
    setWorkshopState(w);
    setTicketsState((prev) => (w ? Math.min(Math.max(prev, 1), Math.max(w.seatsLeft, 1)) : 1));
  };

  const setTickets = (v: number) => {
    const max = workshop ? Math.max(workshop.seatsLeft, 1) : 1;
    setTicketsState(Math.min(Math.max(Math.round(v) || 1, 1), max));
  };

  const setAllTickets = () => {
    if (workshop) setTicketsState(Math.max(workshop.seatsLeft, 1));
  };

  return {
    workshop,
    setWorkshop,
    fullName,
    setFullName,
    email,
    setEmail,
    tickets,
    setTickets,
    setAllTickets,
    mode,
    setMode,
    reason,
    setReason,
    isSubmitting,
    setIsSubmitting,
    error,
    setError,
    money: computeMoneySummary(workshop, tickets, mode, serviceFeeEnabled),
    validationError: validateCreateBookingForm({ workshop, email, tickets, mode, reason }),
  };
}
